import { motion } from "framer-motion";
import { MapPin, Users, Home, Landmark, Mountain, Calendar } from "lucide-react";
import { IMAGES } from "@/assets/images";

export default function LaVille() {
  const stats = [
    { icon: Users, label: "Population", value: "4 200 habitants" },
    { icon: Home, label: "Superficie", value: "13,5 km²" },
    { icon: Mountain, label: "Altitude", value: "220 m" },
    { icon: Calendar, label: "Fondation", value: "XIe siècle" },
  ];

  const patrimoine = [
    {
      title: "Musée de la Mine",
      description: "Découvrez l'histoire minière de Gréasque à travers un parcours authentique dans les galeries souterraines.",
      image: IMAGES.VILLAGE_5,
    },
    {
      title: "Église Saint-Sauveur",
      description: "Monument historique du XIIe siècle, témoin de l'architecture romane provençale.",
      image: IMAGES.MAIRIE_2,
    },
    {
      title: "Place du Village",
      description: "Cœur historique de Gréasque, lieu de rencontres et d'animations tout au long de l'année.",
      image: IMAGES.PLACE_1,
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="relative h-[60vh] min-h-[400px] flex items-center justify-center overflow-hidden"
      >
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.VILLAGE_5}
            alt="Village de Gréasque"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background/70" />
        </div>
        <div className="relative z-10 container mx-auto px-4 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-5xl md:text-7xl font-bold mb-6 text-foreground"
          >
            La Ville de Gréasque
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto"
          >
            Un village provençal au riche patrimoine minier et culturel
          </motion.p>
        </div>
      </motion.div>

      <section className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="text-center"
                >
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
                    <Icon className="w-8 h-8 text-primary" />
                  </div>
                  <div className="text-3xl font-bold text-foreground mb-2">{stat.value}</div>
                  <div className="text-sm text-muted-foreground">{stat.label}</div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-4xl font-bold mb-8 text-center text-foreground">Histoire et Patrimoine</h2>
            <div className="space-y-6 text-lg text-muted-foreground leading-relaxed">
              <p>
                Gréasque est une commune française située dans le département des Bouches-du-Rhône, en région Provence-Alpes-Côte d'Azur. Son histoire est intimement liée à l'exploitation minière qui a façonné son identité et son développement au cours des siècles.
              </p>
              <p>
                Le village tire son nom du provençal "Gréasca", qui signifie "terre grasse". Dès le Moyen Âge, Gréasque se développe autour de son église et de son château. Mais c'est au XIXe siècle que la commune connaît un essor considérable avec l'exploitation intensive du charbon.
              </p>
              <p>
                L'architecture provençale traditionnelle se mêle harmonieusement aux vestiges de l'ère industrielle, créant un patrimoine unique qui témoigne de la richesse historique de la commune. Les ruelles étroites du vieux village, les façades en pierre ocre et les toits de tuiles rouges caractéristiques de la Provence offrent un cadre authentique et préservé.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto mb-16"
          >
            <h2 className="text-4xl font-bold mb-8 text-center text-foreground">Géographie et Situation</h2>
            <div className="flex items-start gap-4 p-6 bg-card rounded-2xl shadow-sm border border-border">
              <MapPin className="w-8 h-8 text-primary flex-shrink-0 mt-1" />
              <div className="space-y-4 text-muted-foreground">
                <p>
                  Située dans le département des Bouches-du-Rhône, Gréasque bénéficie d'une position privilégiée à proximité d'Aix-en-Provence, à seulement 15 kilomètres du centre-ville. Cette proximité avec la capitale historique de la Provence offre aux habitants un accès facile aux services, à la culture et aux opportunités économiques de la métropole.
                </p>
                <p>
                  Le village s'étend sur 13,5 km² et culmine à 220 mètres d'altitude. Son territoire vallonné offre des panoramas remarquables sur la campagne provençale environnante et les massifs montagneux au loin. Le climat méditerranéen, avec ses étés chauds et secs et ses hivers doux, contribue à la qualité de vie exceptionnelle de la commune.
                </p>
                <p>
                  Gréasque est bien desservie par les axes routiers, notamment la D6 qui la relie à Aix-en-Provence et Gardanne. Cette accessibilité en fait un lieu de résidence apprécié pour ceux qui travaillent dans l'agglomération aixoise tout en recherchant le calme d'un village provençal.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-24">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-4 text-foreground">Patrimoine Culturel</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Découvrez les trésors architecturaux et culturels qui font la richesse de Gréasque
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {patrimoine.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group"
              >
                <div className="relative overflow-hidden rounded-2xl mb-6 aspect-[4/3]">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
                <div className="flex items-start gap-3">
                  <Landmark className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-2xl font-semibold mb-3 text-foreground group-hover:text-primary transition-colors">
                      {item.title}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">{item.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-primary/5">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
          >
            <h2 className="text-4xl font-bold mb-8 text-foreground">Le Musée de la Mine</h2>
            <div className="space-y-6 text-lg text-muted-foreground leading-relaxed">
              <p>
                Le Musée de la Mine de Gréasque est un site incontournable qui retrace l'épopée minière de la région. Installé dans les anciennes installations du puits Hély d'Oissel, il offre une plongée authentique dans l'univers des mineurs qui ont façonné l'histoire de la commune.
              </p>
              <p>
                Les visiteurs peuvent découvrir les galeries souterraines, les machines d'extraction, les outils des mineurs et comprendre les conditions de travail difficiles de ces hommes qui ont extrait le charbon pendant plus d'un siècle. Le musée propose également des expositions temporaires et des animations pédagogiques pour les scolaires.
              </p>
              <p>
                Ce patrimoine industriel exceptionnel est un témoignage vivant de l'histoire sociale et économique de la Provence et constitue un lieu de mémoire essentiel pour les générations futures.
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
